/**
 * 카카오(다음) 우편번호 검색 — 키가 필요 없는 공개 스크립트(견적 설계 §5-4·§12).
 *
 * 스크립트는 주소 검색을 처음 누를 때만 주입한다(load-payment-widgets 와 같은 지연 로딩).
 * 화면은 SDK 데이터 전체가 아니라 `PostcodeResult` 두 칸만 받는다 — SDK 타입이 상위 계층에 새지 않는다.
 */
export interface PostcodeResult {
  readonly postalCode: string;
  readonly address1: string;
}

const SCRIPT_URL = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
const LOAD_FAILURE_MESSAGE = '주소 검색을 불러오지 못했습니다.';
/** 스크립트가 load·error 어느 쪽도 알리지 않을 때(네트워크 정체 등) 실패로 보는 시간. */
const LOAD_TIMEOUT_MS = 15_000;

export interface OpenPostcodeOptions {
  /** 검색창을 연 직후 — 호출부는 이때 "불러오는 중" 잠금을 푼다(창이 응답하지 않아도 버튼이 잠긴 채 남지 않게). */
  readonly onOpen?: () => void;
}

/** SDK 가 oncomplete 로 주는 값 중 우리가 읽는 두 칸 — address 는 사용자가 고른 도로명·지번 주소다. */
interface PostcodeData {
  readonly zonecode: string;
  readonly address: string;
}

interface PostcodeOptions {
  readonly oncomplete: (data: PostcodeData) => void;
  /** 선택 없이 닫으면 FORCE_CLOSE, 선택을 마치고 닫히면 COMPLETE_CLOSE. */
  readonly onclose?: (state: 'FORCE_CLOSE' | 'COMPLETE_CLOSE') => void;
}

type PostcodeConstructor = new (options: PostcodeOptions) => { open: () => void };

type PostcodeWindow = Window & { daum?: { Postcode?: PostcodeConstructor } };

const readPostcode = (): PostcodeConstructor | null =>
  (window as PostcodeWindow).daum?.Postcode ?? null;

let loading: Promise<PostcodeConstructor> | null = null;

const loadPostcodeScript = (): Promise<PostcodeConstructor> => {
  const loaded = readPostcode();

  if (loaded) {
    return Promise.resolve(loaded);
  }

  if (loading) {
    return loading;
  }

  loading = new Promise<PostcodeConstructor>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;

    let settled = false;
    const fail = (): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      // 실패한 태그를 치워 다음 호출이 다시 주입할 수 있게 한다.
      loading = null;
      script.remove();
      reject(new Error(LOAD_FAILURE_MESSAGE));
    };
    const timeout = window.setTimeout(fail, LOAD_TIMEOUT_MS);

    script.addEventListener('load', () => {
      const postcodeConstructor = readPostcode();
      if (!postcodeConstructor) {
        fail();
        return;
      }
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      resolve(postcodeConstructor);
    });
    script.addEventListener('error', fail);
    document.head.append(script);
  });

  return loading;
};

/**
 * 우편번호 검색창을 연다. 주소를 고르면 우편번호·기본 주소를, 선택 없이 닫으면 null 을 돌려준다.
 * 스크립트가 15초 안에 준비되지 않으면 거부한다. 창을 연 직후 `onOpen` 을 부른다.
 */
export const openPostcode = async ({
  onOpen,
}: OpenPostcodeOptions = {}): Promise<PostcodeResult | null> => {
  const Postcode = await loadPostcodeScript();

  return new Promise<PostcodeResult | null>((resolve) => {
    let completed = false;

    new Postcode({
      oncomplete: (data) => {
        completed = true;
        resolve({ postalCode: data.zonecode, address1: data.address });
      },
      // 선택을 마쳐도 onclose 가 따라오므로 완료 뒤의 닫힘은 무시한다.
      onclose: () => {
        if (!completed) {
          resolve(null);
        }
      },
    }).open();
    onOpen?.();
  });
};
